// Shopify Storefront API client (public token, used from the frontend)
const DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
const TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
const API_VERSION = "2024-10";

const ENDPOINT = `https://${DOMAIN}/api/${API_VERSION}/graphql.json`;

export async function shopifyFetch(query, variables = {}) {
  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": TOKEN,
      Accept: "application/json",
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) {
    throw new Error(`Shopify API error: ${res.status}`);
  }
  const json = await res.json();
  if (json.errors) {
    throw new Error(json.errors.map((e) => e.message).join(", "));
  }
  return json.data;
}

const PRODUCT_FRAGMENT = `
  id
  handle
  title
  description
  descriptionHtml
  tags
  availableForSale
  productType
  vendor
  priceRange {
    minVariantPrice { amount currencyCode }
    maxVariantPrice { amount currencyCode }
  }
  compareAtPriceRange {
    minVariantPrice { amount currencyCode }
  }
  featuredImage { url altText width height }
  images(first: 8) {
    edges { node { url altText width height } }
  }
  options { id name values }
  variants(first: 50) {
    edges {
      node {
        id
        title
        availableForSale
        price { amount currencyCode }
        compareAtPrice { amount currencyCode }
        selectedOptions { name value }
        image { url altText }
      }
    }
  }
`;

export async function listProducts({ first = 24, after = null, query = null } = {}) {
  const gql = `
    query Products($first: Int!, $after: String, $query: String) {
      products(first: $first, after: $after, query: $query, sortKey: BEST_SELLING) {
        edges { cursor node { ${PRODUCT_FRAGMENT} } }
        pageInfo { hasNextPage endCursor }
      }
    }
  `;
  const data = await shopifyFetch(gql, { first, after, query });
  return {
    products: data.products.edges.map((e) => e.node),
    pageInfo: data.products.pageInfo,
  };
}

// Fetch every product in the store by paginating through the Storefront API.
// onProgress(count) is optional and called after each page so the UI can
// display a live "syncing X products..." indicator.
export async function listAllProducts({ pageSize = 250, onProgress = null, query = null } = {}) {
  const all = [];
  let after = null;
  let hasNext = true;
  // Safety cap to avoid runaway loops if Shopify returns unexpected paging.
  let pages = 0;
  const MAX_PAGES = 40; // 40 * 250 = 10,000 products
  while (hasNext && pages < MAX_PAGES) {
    const { products, pageInfo } = await listProducts({ first: pageSize, after, query });
    all.push(...products);
    hasNext = pageInfo?.hasNextPage;
    after = pageInfo?.endCursor || null;
    pages += 1;
    if (typeof onProgress === "function") {
      try { onProgress(all.length); } catch { /* ignore */ }
    }
    if (!after) break;
  }
  return all;
}

export async function getProductByHandle(handle) {
  const gql = `
    query Product($handle: String!) {
      product(handle: $handle) { ${PRODUCT_FRAGMENT} }
    }
  `;
  const data = await shopifyFetch(gql, { handle });
  return data.product;
}

export async function listCollections({ first = 20 } = {}) {
  const gql = `
    query Collections($first: Int!) {
      collections(first: $first) {
        edges {
          node {
            id
            handle
            title
            description
            image { url altText }
          }
        }
      }
    }
  `;
  const data = await shopifyFetch(gql, { first });
  return data.collections.edges.map((e) => e.node);
}

export async function getCollectionByHandle(handle, { first = 48 } = {}) {
  const gql = `
    query Collection($handle: String!, $first: Int!) {
      collection(handle: $handle) {
        id
        title
        description
        products(first: $first) {
          edges { node { ${PRODUCT_FRAGMENT} } }
        }
      }
    }
  `;
  const data = await shopifyFetch(gql, { handle, first });
  if (!data.collection) return null;
  return {
    ...data.collection,
    products: data.collection.products.edges.map((e) => e.node),
  };
}

// Fetch every product ID that belongs to a collection (paginated).
// Returns a Set of product IDs (Shopify GIDs) for fast membership checks.
export async function getCollectionProductIds(handle, { pageSize = 250 } = {}) {
  const ids = new Set();
  let after = null;
  let hasNext = true;
  let pages = 0;
  const MAX_PAGES = 40;
  while (hasNext && pages < MAX_PAGES) {
    const gql = `
      query CollectionIds($handle: String!, $first: Int!, $after: String) {
        collection(handle: $handle) {
          products(first: $first, after: $after) {
            edges { node { id } }
            pageInfo { hasNextPage endCursor }
          }
        }
      }
    `;
    const data = await shopifyFetch(gql, { handle, first: pageSize, after });
    const col = data.collection;
    if (!col) break;
    col.products.edges.forEach((e) => ids.add(e.node.id));
    hasNext = col.products.pageInfo?.hasNextPage;
    after = col.products.pageInfo?.endCursor || null;
    pages += 1;
    if (!after) break;
  }
  return ids;
}

// Cart API
export async function createCart(lines = []) {
  const gql = `
    mutation CartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart { ${CART_FRAGMENT} }
        userErrors { field message }
      }
    }
  `;
  const data = await shopifyFetch(gql, { input: { lines } });
  if (data.cartCreate.userErrors?.length) {
    throw new Error(data.cartCreate.userErrors.map((e) => e.message).join(", "));
  }
  return data.cartCreate.cart;
}

export async function addCartLines(cartId, lines) {
  const gql = `
    mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart { ${CART_FRAGMENT} }
        userErrors { field message }
      }
    }
  `;
  const data = await shopifyFetch(gql, { cartId, lines });
  if (data.cartLinesAdd.userErrors?.length) {
    throw new Error(data.cartLinesAdd.userErrors.map((e) => e.message).join(", "));
  }
  return data.cartLinesAdd.cart;
}

export async function updateCartLines(cartId, lines) {
  const gql = `
    mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart { ${CART_FRAGMENT} }
        userErrors { field message }
      }
    }
  `;
  const data = await shopifyFetch(gql, { cartId, lines });
  return data.cartLinesUpdate.cart;
}

export async function removeCartLines(cartId, lineIds) {
  const gql = `
    mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart { ${CART_FRAGMENT} }
        userErrors { field message }
      }
    }
  `;
  const data = await shopifyFetch(gql, { cartId, lineIds });
  return data.cartLinesRemove.cart;
}

export async function getCart(cartId) {
  const gql = `
    query Cart($cartId: ID!) {
      cart(id: $cartId) { ${CART_FRAGMENT} }
    }
  `;
  const data = await shopifyFetch(gql, { cartId });
  return data.cart;
}

const CART_FRAGMENT = `
  id
  checkoutUrl
  totalQuantity
  cost {
    totalAmount { amount currencyCode }
    subtotalAmount { amount currencyCode }
    totalTaxAmount { amount currencyCode }
  }
  lines(first: 50) {
    edges {
      node {
        id
        quantity
        merchandise {
          ... on ProductVariant {
            id
            title
            price { amount currencyCode }
            image { url altText }
            product { handle title }
            selectedOptions { name value }
          }
        }
      }
    }
  }
`;

export function formatPrice(amount, currency = "USD") {
  if (amount == null) return "";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(Number(amount));
  } catch {
    return `$${Number(amount).toFixed(2)}`;
  }
}
